import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Container, Form, Spinner } from "react-bootstrap";
import { Context } from "../App";
import * as Constants from "../api/constants";
import { normalizeTheatersList } from "../utils/normalizeTheaters";
import { enrichSlotFromTheatre, resolveMaxGuests } from "../utils/slotGuests";
import {
  formatDateDisplayEnglish,
  toDateInputValue,
} from "../utils/eventDate";
import {
  persistAddonMeta,
  saveBookingFlow,
} from "../utils/bookingFlowStorage";
import { normalizeAddonMetaPayload } from "../utils/normalizeAddonMeta";
import { mergeSlotWithOffer } from "../utils/slotTimeOffers";
import { useCms } from "../context/CmsContext";
import { isSlotDisabled } from "../utils/slotAvailability";
import "../partyBossTheme.css";

const ADVANCE_INCL_FEE = 700;

const fieldClass =
  "rounded-lg border border-white/20 bg-[#12081f] px-2.5 py-1.5 text-sm text-[#f1f5f9] shadow-[inset_0_0_0_1px_rgba(236,72,153,0.12)] placeholder:text-slate-500 focus:border-pink-400 focus:outline-none focus:ring-2 focus:ring-pink-500/35";

const labelClass =
  "mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-[#c9c1d9]";

const overviewRowClass =
  "flex min-w-0 items-start gap-1.5 px-2 py-1 sm:gap-2 sm:px-3 sm:py-1";

const overviewIconClass =
  "mt-0.5 shrink-0 text-sm leading-none text-[#ffd166] sm:text-base";

const bookingGridClass = "grid gap-x-3 gap-y-2 sm:grid-cols-2 sm:items-start";

const cardShell =
  "overflow-hidden rounded-2xl border border-white/15 bg-[#0a0614] shadow-[0_12px_40px_-16px_rgba(0,0,0,0.45)] ring-1 ring-white/[0.06]";

/** Visual strikethrough in native `<select>` options (CSS strike not supported reliably). */
function strikethroughOptionLabel(text) {
  const base = String(text || "").trim() || "";
  return [...base].map((ch) => (ch === " " ? " " : `${ch}\u0336`)).join("");
}

/** Prefer ~9:30 AM among selectable slots; otherwise first available. */
function pickDefaultSelectableSlot(slots, dateStr) {
  if (!Array.isArray(slots) || !slots.length) return null;
  const open = slots.filter((s) => !isSlotDisabled(s, dateStr));
  if (!open.length) return null;
  const nine = open.find((s) => {
    const t = String(s?.slotTime || "").toLowerCase();
    return /\b9\s*:\s*30\b/.test(t) || /\b09\s*:\s*30\b/.test(t);
  });
  return nine || open[0];
}

function captureTrafficSource(params) {
  try {
    const tid = params.get("theatreId");
    const rawSrc = params.get("src");
    let src =
      typeof rawSrc === "string" && rawSrc.trim()
        ? rawSrc.trim().slice(0, 128)
        : "";
    if (tid && String(tid).trim() !== "") {
      const id = String(tid).trim();
      if (/^\d+$/.test(id)) {
        sessionStorage.setItem("pb_quick_theatre_id", id);
      }
      if (!src) src = `qr_theatre_${id}`.slice(0, 128);
    } else if (!src) {
      src = "qr_quick_book_generic";
    }
    sessionStorage.setItem("pb_booking_traffic_source", src);
  } catch (_) {
    /* ignore */
  }
}

export default function QuickBookPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { cms } = useCms();
  const bf = cms.bookingForm;

  const {
    setContext,
    setselectedTheaterByDate,
    SetSelectedTime,
    setTotalCost,
    setAddonMetaData,
    addonMetaData,
  } = useContext(Context);

  const theatreIdParam = params.get("theatreId");
  const theatreNameParam = (params.get("theatreName") || "").trim();

  const [eventDate, setEventDate] = useState(() =>
    toDateInputValue(new Date())
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [slotBanner, setSlotBanner] = useState(null);
  const [resolvedTheatre, setResolvedTheatre] = useState(null);
  const [slotOptions, setSlotOptions] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [bookingName, setBookingName] = useState("");
  const [persons, setPersons] = useState(2);
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [eventId, setEventId] = useState("");

  const [extraPersonPrice, setExtraPersonPrice] = useState(0);

  useEffect(() => {
    document.body.classList.add("party-boss-home");
    captureTrafficSource(params);
    return () => document.body.classList.remove("party-boss-home");
  }, [params]);

  useEffect(() => {
    const tid = Number(theatreIdParam);
    if (!theatreIdParam || !Number.isFinite(tid) || tid <= 0) {
      setLoadError("Missing or invalid theatre. Open this page from your venue QR link.");
      setLoading(false);
      setResolvedTheatre(null);
      setSlotOptions([]);
      setSelectedSlot(null);
      return;
    }

    const controller = new AbortController();
    const dateStr = toDateInputValue(eventDate);

    setLoading(true);
    setLoadError(null);
    setSlotBanner(null);

    axios
      .all([
        axios.post(Constants.GET_THEATERS_LIST, { date: dateStr }, {
          signal: controller.signal,
        }),
        axios.get(Constants.GET_META_DATA_ADDONS, {
          signal: controller.signal,
        }),
      ])
      .then(
        axios.spread((slotsRes, metaRes) => {
          const meta = normalizeAddonMetaPayload(metaRes?.data ?? {});
          setAddonMetaData(meta);
          persistAddonMeta(meta);

          const list = normalizeTheatersList(slotsRes?.data);
          const theatre = list.find((t) => Number(t?.id) === tid);

          if (!theatre) {
            setResolvedTheatre(null);
            setSlotOptions([]);
            setSelectedSlot(null);
            setLoadError(
              "This theatre has no published slots on the selected date. Pick another date or contact us."
            );
            return;
          }

          setLoadError(null);

          const rawSlots = Array.isArray(theatre.timeslots)
            ? theatre.timeslots
            : [];
          const slotOffers = Array.isArray(meta?.slotOffers) ? meta.slotOffers : [];
          const enriched = rawSlots.map((s) =>
            mergeSlotWithOffer(enrichSlotFromTheatre(s, theatre), tid, dateStr, slotOffers)
          );

          setResolvedTheatre(theatre);
          setSlotOptions(enriched);
          const def = pickDefaultSelectableSlot(enriched, dateStr);
          setSelectedSlot(def);

          setselectedTheaterByDate(theatre);
          SetSelectedTime(def);
          setContext((prev) => ({
            ...(prev && typeof prev === "object" ? prev : {}),
            eventDate: dateStr,
          }));
          if (!enriched.length) {
            setSlotBanner(
              "No time slots are configured for this venue on this date."
            );
          } else if (!def) {
            setSlotBanner(
              "Every slot on this date is already booked or has ended. Choose another date."
            );
          } else {
            setSlotBanner(null);
          }
        })
      )
      .catch((error) => {
        if (
          controller.signal.aborted ||
          error?.code === "ERR_CANCELED" ||
          error?.name === "CanceledError"
        ) {
          return;
        }
        setLoadError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load slots. Please try again."
        );
        setResolvedTheatre(null);
        setSlotOptions([]);
        setSelectedSlot(null);
        setSlotBanner(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [
    eventDate,
    theatreIdParam,
    setAddonMetaData,
    SetSelectedTime,
    setContext,
    setselectedTheaterByDate,
  ]);

  useEffect(() => {
    const first = addonMetaData?.eventTypes?.find((e) => e?.isActive === "Y");
    if (first?.id != null && eventId === "") {
      setEventId(String(first.id));
    }
  }, [addonMetaData, eventId]);

  const eventDateStr = useMemo(
    () => toDateInputValue(eventDate),
    [eventDate]
  );

  const minSelectableDate = toDateInputValue(new Date());
  const maxCalendarDate = `${new Date().getFullYear() + 1}-12-31`;

  const packageGuests = Number(persons);

  const effectiveMaxGuests = useMemo(
    () => resolveMaxGuests(selectedSlot, resolvedTheatre),
    [selectedSlot, resolvedTheatre]
  );

  const personOptions = useMemo(
    () => Array.from({ length: effectiveMaxGuests }, (_, i) => i + 1),
    [effectiveMaxGuests]
  );

  useEffect(() => {
    if (!selectedSlot) return;
    const maxG = effectiveMaxGuests;
    const n = Math.min(Math.max(1, packageGuests), maxG);
    if (n > selectedSlot.defaultCapacity) {
      const extra =
        (n - selectedSlot.defaultCapacity) *
          (selectedSlot.extraPerPerson ?? 0) +
        (selectedSlot.basePrice ?? 0);
      setExtraPersonPrice(extra);
      setTotalCost(extra);
    } else {
      const base = selectedSlot.basePrice ?? 0;
      setExtraPersonPrice(base);
      setTotalCost(base);
    }
  }, [selectedSlot, persons, setTotalCost, packageGuests, effectiveMaxGuests]);

  useEffect(() => {
    const maxG = effectiveMaxGuests;
    const p = Number(persons);
    const clamped = Math.min(Math.max(1, p), maxG);
    if (clamped !== p) setPersons(clamped);
  }, [effectiveMaxGuests, persons]);

  const lineTotal = extraPersonPrice ?? 0;
  const balanceAtVenue = Math.max(0, lineTotal - ADVANCE_INCL_FEE);

  const overviewDate = useMemo(
    () => formatDateDisplayEnglish(eventDateStr),
    [eventDateStr]
  );

  const displayTheatreName =
    resolvedTheatre?.name ||
    resolvedTheatre?.theatreName ||
    resolvedTheatre?.theaterName ||
    theatreNameParam ||
    "—";

  const onSlotChange = (e) => {
    const id = Number(e.target.value);
    const slot = slotOptions.find((s) => Number(s.id) === id) || null;
    setSelectedSlot(slot);
    SetSelectedTime(slot);
  };

  const onProceed = (e) => {
    e.preventDefault();
    if (!eventId || !selectedSlot?.id) return;
    if (isSlotDisabled(selectedSlot, eventDateStr)) return;
    const dateStr = eventDateStr;
    try {
      sessionStorage.setItem("pb_review_back_path", "/quick-book");
    } catch (_) {
      /* ignore */
    }
    saveBookingFlow({
      slotId: Number(selectedSlot.id),
      eventId: Number(eventId),
      personsCount: Number(persons),
      bookingName: bookingName.trim(),
      whatsapp: whatsapp.trim(),
      email: email.trim(),
      eventDate: dateStr,
      theatreBaseCost: lineTotal,
      selectedTheaterSnapshot: {
        id: resolvedTheatre?.id,
        name:
          resolvedTheatre?.name ||
          resolvedTheatre?.theatreName ||
          displayTheatreName,
      },
      selectedTimeSnapshot: {
        id: selectedSlot?.id,
        defaultCapacity: selectedSlot?.defaultCapacity,
        extraPerPerson: selectedSlot?.extraPerPerson,
        basePrice: selectedSlot?.basePrice,
        slotTime: selectedSlot?.slotTime,
      },
      slotOffer:
        selectedSlot?.slotOfferActive
          ? {
              active: true,
              headline: selectedSlot.slotOfferHeadline || "",
              offerBasePrice: Number(selectedSlot.basePrice ?? 0),
              regularSlotBasePrice: Number(
                selectedSlot.regularSlotBasePrice ?? 0
              ),
              offerPriceKind: selectedSlot.slotOfferPriceKind || "RS",
              offerRawValue: Number(selectedSlot.slotOfferRawValue ?? 0),
              slotOfferDisplayLabel: selectedSlot.slotOfferDisplayLabel || "",
              id: selectedSlot.slotTimeOfferId ?? null,
            }
          : null,
      selectedAddons: {},
      nameOnCake: "",
      reviewTotalCost: lineTotal,
      wantsDecoration: "No",
    });
    navigate("/booknow/review");
  };

  const canProceed =
    resolvedTheatre?.id &&
    selectedSlot?.id &&
    !isSlotDisabled(selectedSlot, eventDateStr) &&
    slotOptions.some((s) => !isSlotDisabled(s, eventDateStr)) &&
    bookingName.trim().length > 0 &&
    /^\d{10}$/.test(whatsapp.trim()) &&
    /^\S+@\S+\.\S+$/.test(email.trim()) &&
    Number(persons) >= 1 &&
    eventId !== "";

  if (!theatreIdParam || !/^\d+$/.test(String(theatreIdParam).trim())) {
    return (
      <section className="party-boss-page w-full bg-[#05020a] pb-16 pt-8 text-white md:pt-10">
        <div className="party-boss-bg relative z-10 mx-auto max-w-lg px-4 text-center">
          <p className="text-[#c9c1d9]">{bf.missingSlotMessage}</p>
          <button
            type="button"
            className="mt-4 rounded-full border border-white/20 bg-white/10 px-6 py-2 font-bold"
            onClick={() => navigate("/booknow")}
          >
            {bf.backToTheatres}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="party-boss-page w-full bg-[#05020a] pb-12 pt-6 text-white selection:bg-pink-500/40 md:pb-16 md:pt-8">
      <div className="party-boss-bg relative z-10 px-3 sm:px-4 md:px-6">
        <p className="text-center text-[10px] font-black uppercase tracking-widest text-[#ffd166] sm:text-xs">
          Quick book
        </p>
        <h1 className="mb-1 text-center text-xl font-black text-white md:text-3xl">
          Book &amp; pay in advance
        </h1>
        <p className="mx-auto mb-4 max-w-xl text-center text-xs text-[#c9c1d9] sm:text-sm">
          Choose your show date and time, then continue to payment. Add-ons can
          be added later from My Booking if you need them.
        </p>

        <Container className="mx-auto max-w-4xl p-0">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <Spinner animation="border" variant="warning" />
              <p className="small text-muted">Loading slots…</p>
            </div>
          ) : loadError && !resolvedTheatre ? (
            <div className="mx-auto max-w-lg rounded-2xl border border-white/15 bg-[#0a0614] p-6 text-center">
              <p className="text-[#c9c1d9]">{loadError}</p>
              <button
                type="button"
                className="mt-4 rounded-xl border border-white/20 bg-white/10 px-5 py-2 font-bold"
                onClick={() => setEventDate(toDateInputValue(new Date()))}
              >
                Use today&apos;s date
              </button>
            </div>
          ) : resolvedTheatre ? (
            <Form id="quick-book-form" onSubmit={onProceed}>
              {slotBanner ? (
                <div className="mb-4 rounded-xl border border-amber-400/35 bg-amber-500/10 px-4 py-3 text-sm text-amber-50">
                  {slotBanner}
                </div>
              ) : null}
              <div className="grid gap-4 md:grid-cols-2 md:items-start">
                <div className="min-w-0">
                  <div className={cardShell}>
                    <div className="border-b border-white/10 bg-gradient-to-r from-pink-600/25 to-violet-700/25 px-3 py-2.5 sm:px-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-[#ffd166]">
                        {bf.overviewLabel}
                      </p>
                    </div>
                    <div className="divide-y divide-white/10 bg-[#12081f]/40 px-3 py-3 text-sm sm:px-4">
                      <div className={overviewRowClass}>
                        <i
                          className={`bi bi-building ${overviewIconClass}`}
                          aria-hidden
                        />
                        <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
                          {bf.overviewTheatreLabel}
                        </span>
                        <span className="min-w-0 flex-1 break-words text-xs font-semibold leading-snug text-white sm:text-sm">
                          {displayTheatreName}
                        </span>
                      </div>

                      <div className="py-3">
                        <Form.Label className={labelClass}>Date</Form.Label>
                        <Form.Control
                          className={fieldClass}
                          type="date"
                          value={eventDateStr}
                          min={minSelectableDate}
                          max={maxCalendarDate}
                          onChange={(e) => {
                            let v = toDateInputValue(e.target.value);
                            if (v < minSelectableDate) v = minSelectableDate;
                            setEventDate(v);
                          }}
                          required
                        />
                      </div>

                      <div className="py-3">
                        <Form.Label className={labelClass}>Time slot</Form.Label>
                        <Form.Select
                          className={fieldClass}
                          value={selectedSlot?.id ?? ""}
                          onChange={onSlotChange}
                          required
                          disabled={
                            !slotOptions.length ||
                            !slotOptions.some(
                              (s) => !isSlotDisabled(s, eventDateStr)
                            )
                          }
                        >
                          {!slotOptions.length ? (
                            <option value="">No slots</option>
                          ) : !slotOptions.some(
                              (s) => !isSlotDisabled(s, eventDateStr)
                            ) ? (
                            <option value="">
                              No open slots — try another date
                            </option>
                          ) : (
                            slotOptions.map((s) => {
                              const dis = isSlotDisabled(s, eventDateStr);
                              const label =
                                String(s.slotTime || "").trim() ||
                                `Slot ${s.id}`;
                              return (
                                <option
                                  key={s.id}
                                  value={s.id}
                                  disabled={dis}
                                >
                                  {dis ? strikethroughOptionLabel(label) : label}
                                </option>
                              );
                            })
                          )}
                        </Form.Select>
                        <p className="mt-1.5 text-[10px] text-[#94a3b8]">
                          Unavailable times appear struck through and cannot be
                          selected. Defaults to the first open slot when
                          available.
                        </p>
                        {selectedSlot?.slotOfferHeadline || selectedSlot?.slotOfferDisplayLabel ? (
                          <p className="mt-1 text-[11px] font-semibold leading-snug text-red-400">
                            {selectedSlot.slotOfferDisplayLabel ? (
                              <span>{selectedSlot.slotOfferDisplayLabel}</span>
                            ) : null}
                            {selectedSlot.slotOfferHeadline ? (
                              <span className="opacity-90">
                                {selectedSlot.slotOfferDisplayLabel ? " — " : ""}
                                {selectedSlot.slotOfferHeadline}
                              </span>
                            ) : null}
                          </p>
                        ) : null}
                      </div>

                      <div className={overviewRowClass}>
                        <i
                          className={`bi bi-calendar3 ${overviewIconClass}`}
                          aria-hidden
                        />
                        <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
                          {bf.overviewDateLabel}
                        </span>
                        <span className="min-w-0 flex-1 text-xs font-semibold leading-snug text-[#ffd166] sm:text-sm">
                          {overviewDate || "—"}
                        </span>
                      </div>
                    </div>

                    <div className="border-t border-white/10 bg-[#0a0614]/40">
                      <div className="border-b border-white/10 bg-gradient-to-r from-pink-600/15 to-violet-700/15 px-3 py-2 sm:px-4">
                        <h2 className="text-[10px] font-black uppercase tracking-widest text-[#ffd166]">
                          {bf.bookingDetailsSectionTitle}
                        </h2>
                      </div>
                      <div className="px-3 pb-3 pt-2.5 sm:px-4 sm:pb-4 sm:pt-3">
                        <div className={bookingGridClass}>
                          <div className="min-w-0 sm:col-span-2">
                            <Form.Label className={labelClass}>
                              {bf.bookingNameLabel}
                            </Form.Label>
                            <Form.Control
                              className={fieldClass}
                              value={bookingName}
                              onChange={(e) => setBookingName(e.target.value)}
                              placeholder={bf.bookingNamePlaceholder}
                              maxLength={40}
                              required
                            />
                          </div>

                          <div className="min-w-0 sm:col-span-2">
                            <Form.Label className={labelClass}>
                              {bf.personsLabel}
                            </Form.Label>
                            <Form.Select
                              className={fieldClass}
                              value={persons}
                              onChange={(e) =>
                                setPersons(Number(e.target.value) || 1)
                              }
                              required
                            >
                              {personOptions.map((n) => (
                                <option key={n} value={n}>
                                  {n}
                                </option>
                              ))}
                            </Form.Select>
                          </div>

                          <div className="min-w-0">
                            <Form.Label className={labelClass}>
                              {bf.whatsappLabel}
                            </Form.Label>
                            <Form.Control
                              className={fieldClass}
                              type="tel"
                              inputMode="numeric"
                              pattern="\d{10}"
                              maxLength={10}
                              placeholder={bf.whatsappPlaceholder}
                              value={whatsapp}
                              onChange={(e) =>
                                setWhatsapp(
                                  e.target.value.replace(/\D/g, "").slice(0, 10)
                                )
                              }
                              required
                            />
                          </div>

                          <div className="min-w-0">
                            <Form.Label className={labelClass}>
                              {bf.emailLabel}
                            </Form.Label>
                            <Form.Control
                              className={fieldClass}
                              type="email"
                              placeholder={bf.emailPlaceholder}
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              required
                            />
                          </div>

                          <div className="min-w-0 sm:col-span-2">
                            <Form.Label className={labelClass}>
                              {bf.eventLabel}
                            </Form.Label>
                            <Form.Select
                              className={fieldClass}
                              value={eventId}
                              onChange={(e) => setEventId(e.target.value)}
                              required
                            >
                              <option value="">Select</option>
                              {addonMetaData?.eventTypes?.map((event) => (
                                <option
                                  key={event?.id}
                                  value={event?.id}
                                  disabled={event?.isActive !== "Y"}
                                >
                                  {event.name}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="min-w-0">
                  <div className={`${cardShell} sticky top-20`}>
                    <div className="border-b border-white/10 bg-gradient-to-r from-pink-600/25 to-violet-700/25 px-3 py-2.5 sm:px-4">
                      <h3 className="text-center text-[10px] font-black uppercase tracking-widest text-[#ffd166]">
                        {bf.summaryTitle}
                      </h3>
                    </div>
                    <div className="space-y-2 px-3 py-3 text-sm sm:px-4">
                      <div className="flex justify-between gap-2 text-[#e8e3f0]">
                        <span className="text-xs">{bf.summaryPersons}</span>
                        <span className="text-right text-xs font-bold text-white">
                          {persons}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2 text-[#e8e3f0]">
                        <span className="text-xs">{bf.summaryPackage}</span>
                        <span className="font-bold text-white">
                          <i className="bi bi-currency-rupee" />
                          {extraPersonPrice ?? 0}
                        </span>
                      </div>
                      <hr className="my-1 border-white/10" />
                      <div className="flex justify-between gap-2 text-base font-black text-white">
                        <span>{bf.summaryTotal}</span>
                        <span className="text-[#ffd166]">
                          <i className="bi bi-currency-rupee" />
                          {lineTotal}
                        </span>
                      </div>
                      <hr className="my-1 border-white/10" />
                      <div className="flex justify-between gap-2 text-xs text-[#c9c1d9]">
                        <span>{bf.summaryAdvance}</span>
                        <span className="font-bold text-[#ffd166]">
                          <i className="bi bi-currency-rupee" />
                          {ADVANCE_INCL_FEE}
                        </span>
                      </div>
                      <p className="text-[10px] leading-snug text-[#94a3b8]">
                        {bf.summaryAdvanceNote}
                      </p>
                      <hr className="my-1 border-white/10" />
                      <div className="flex justify-between gap-2 text-xs text-[#e8e3f0]">
                        <span>{bf.summaryBalance}</span>
                        <span className="font-bold text-emerald-400">
                          <i className="bi bi-currency-rupee" />
                          {balanceAtVenue}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 border-t border-white/10 px-3 py-3 sm:px-4">
                      <button
                        type="button"
                        className="flex-1 rounded-xl border border-white/20 bg-white/5 py-2.5 text-sm font-bold text-white transition hover:bg-white/10"
                        onClick={() => navigate("/booknow")}
                      >
                        {bf.backToTheatres}
                      </button>
                      <button
                        type="submit"
                        form="quick-book-form"
                        className="flex-[1.35] rounded-xl bg-gradient-to-r from-pink-500 to-violet-600 py-2.5 text-sm font-black text-white shadow-md disabled:cursor-not-allowed disabled:opacity-45"
                        disabled={!canProceed}
                      >
                        Proceed to pay in advance
                      </button>
                    </div>
                    <p className="border-t border-white/10 px-3 py-2.5 text-center text-[10px] leading-snug text-[#94a3b8] sm:px-4">
                      Continues to review and online payment (same checkout as
                      the main booking flow).
                    </p>
                  </div>
                </div>
              </div>
            </Form>
          ) : (
            <div className="mx-auto max-w-lg rounded-2xl border border-white/15 bg-[#0a0614] p-6 text-center text-sm text-[#c9c1d9]">
              Unable to load this booking. Please refresh or use the QR link again.
            </div>
          )}
        </Container>
      </div>
    </section>
  );
}
